import { CAPACITY_UNITS } from './tea';
import { blankDefaults } from './defaults';
import { normalizeInput, normalizeOutput, withName } from './payload';

// The inverse of payload.js: turns a saved equation module (the `data` the
// custom_models endpoint returns to anyone who can use it) back into builder
// form values for Duplicate.

// The saved description is the user's text followed by blocks the builder
// and backend append on save — the balance (`buildFullDescription`) and the
// authorship lines (`build_description` in app/serializers/custom_models.py).
// Both are regenerated on the next save, so only the user's text is editable.
const GENERATED_BLOCKS = /\n*\*\*(Formulation|Created by:)\*\*[\s\S]*$/;

export function userDescription(description) {
  return (description || '').replace(GENERATED_BLOCKS, '').trim();
}

// Saved quantities keep the unit structs the builder sent, so a capacity
// unit is found by its numerator / denominator names.
const unitKey = unit =>
  ['numerator', 'denominator'].map(part => (unit?.[part] ?? []).map(u => u.name).join('*')).join('/');

const capacityTokenFor = unit => CAPACITY_UNITS.find(u => unitKey(u.struct) === unitKey(unit))?.token;

const scalarOf = quantity => quantity?.scalar ?? 0;

function teaToForm(tea) {
  const form = blankDefaults().tea;
  if (!tea) return form;

  if (tea.tea_type === 'simple') {
    const simple_costs = {};
    (tea.data?.levelized_costs ?? []).forEach(({ product, cost }) => {
      simple_costs[product] = scalarOf(cost);
    });
    return { ...form, mode: 'simple', simple_costs };
  }

  if (tea.tea_type === 'dcfa') {
    const d = tea.data ?? {};
    return {
      ...form,
      mode: 'dcfa',
      reference_product: d.reference_product ?? '',
      reference_capacity: scalarOf(d.reference_capacity),
      // Unmatched units stay blank so validation asks the user to pick one.
      reference_capacity_unit: capacityTokenFor(d.reference_capacity?.unit) ?? '',
      capacity_factor: scalarOf(d.capacity_factor),
      capital_cost: scalarOf(d.capital_cost),
      fixed_operating_costs: scalarOf(d.fixed_operating_costs),
      fixed_maintenance_costs: scalarOf(d.fixed_maintenance_costs),
      variable_operating_costs: scalarOf(d.variable_operating_costs),
      scaling_exponent: d.scaling_exponent ?? 0,
    };
  }

  return form;
}

// Rows keep their saved `name`: the balance refers to rows by those names,
// and re-deriving them from labels that have since been edited would leave it
// pointing at nothing. Names only have to be unique within a module, so the
// copy can reuse them.
export function specToForm(customModel, projectId) {
  const data = customModel?.data ?? {};
  const label = data.label ?? '';
  // A copy of someone else's module starts private to its new owner: they
  // may not belong to the projects the original is shared with. With no
  // projects to carry over, sharing means the project the copy is made in,
  // as it does on create.
  const keepSharing = !!customModel?.is_creator;
  const sharedProjectIds = (keepSharing && customModel?.project_ids) || [];

  return {
    // Not "(copy)": parentheticals are dropped from the slug, so that would
    // collide with the original's node type name.
    label: `${label} copy`,
    description: userDescription(data.description),
    permissions: (keepSharing && customModel?.permissions) || 'creator',
    project_ids: sharedProjectIds.length ? sharedProjectIds : [projectId],
    inputs: (data.inputs ?? []).map(withName(normalizeInput)),
    outputs: (data.outputs ?? []).map(withName(normalizeOutput)),
    params: (data.params ?? []).map(
      withName(p => ({
        label: p.label,
        type: p.type ?? 'numeric',
        unit: p.unit ?? 'scalar',
        // Booleans ride the wire as 1 / 0; the form's <select> holds strings.
        default: p.type === 'boolean' ? String(!!p.default) : p.default,
      })),
    ),
    balance: data.balance ?? '',
    tea: teaToForm(data.tea),
  };
}
